import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// XMLHttpRequest-based request with httpOnly cookie support
const xhrRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);
    xhr.withCredentials = true;
    
    // Set headers
    const headers = options.headers || {};
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onload = () => {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        data = { detail: 'Invalid response' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ ok: true, data });
      } else {
        resolve({ ok: false, data, status: xhr.status });
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timeout'));
    xhr.timeout = 30000;
    xhr.send(options.body);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCurrentUser = useCallback(async () => {
    try {
      const result = await xhrRequest(`${API_URL}/api/auth/me`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (result.ok) {
        setUser(result.data);
      } else {
        setUser(null);
      }
    } catch (error) {
      logger.error('Failed to fetch user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email, password, mfaCode = null) => {
    try {
      const requestBody = { email, password };
      if (mfaCode) {
        requestBody.mfa_code = mfaCode;
      }
      
      const result = await xhrRequest(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Login failed');
      }

      const data = result.data;
      
      // Check if MFA is required
      if (data.mfa_required) {
        return { 
          success: false, 
          mfaRequired: true, 
          userId: data.user_id,
          message: data.message || 'Please enter your 6-digit MFA code'
        };
      }
      
      // Login successful — cookie is set by the server automatically
      setUser(data.user);
      
      return { success: true, user: data.user, mfaEnabled: data.user?.mfa_enabled };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const result = await xhrRequest(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      if (!result.ok) {
        throw new Error(result.data.detail || 'Registration failed');
      }

      // After registration, auto-login to get the httpOnly cookie
      const loginResult = await xhrRequest(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userData.email, password: userData.password })
      });

      if (!loginResult.ok) {
        throw new Error(loginResult.data.detail || 'Auto-login failed');
      }

      setUser(loginResult.data.user);
      
      return { success: true, user: loginResult.data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await xhrRequest(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (e) {
      // Logout even if the server call fails
    }
    setUser(null);
    navigate('/login');
  };

  // Expose fetchCurrentUser as refreshUser for components that need to refresh user state
  const refreshUser = fetchCurrentUser;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    isDoctor: user?.role === 'doctor',
    isPatient: user?.role === 'user' || user?.role === 'patient'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
